import { apiJson } from '../../../shared/api/http';

/** Super admin only: quiet mode, permanent deletion, the Data clean-up inventory. */

export type QuietChannel = 'email' | 'in_app' | 'slack' | 'webhook' | 'reminder_run' | 'calendar';

export interface QuietModeStatus {
  on: boolean;
  changed_at?: string | null;
  changed_by?: string | null;
  suppressed: Record<QuietChannel, number>;
  last_suppressed_at?: string | null;
  channels: QuietChannel[];
}

/** Fired on window when a super admin flips quiet mode, so the banner follows without a reload. */
export const QUIET_MODE_EVENT = 'kudos:quiet-mode';

export function fetchQuietMode(): Promise<QuietModeStatus> {
  return apiJson('/api/admin/platform/quiet-mode', { auth: true });
}

/** Whether quiet mode is on, for anyone who manages things (admins, HR, editors, super admins). */
export function fetchQuietModeStatus(): Promise<{ on: boolean; can_manage: boolean }> {
  return apiJson('/api/quiet-mode', { auth: true });
}

export async function setQuietMode(on: boolean): Promise<QuietModeStatus> {
  const status = await apiJson('/api/admin/platform/quiet-mode', { auth: true, method: 'PUT', body: { on } });
  window.dispatchEvent(new CustomEvent(QUIET_MODE_EVENT, { detail: { on: status?.on } }));
  return status;
}

export type PurgeType =
  | 'event' | 'event_series' | 'course' | 'workshop' | 'kudos' | 'comment'
  | 'badge_award' | 'points_transaction' | 'certificate' | 'user';

export interface PurgeTarget {
  type: PurgeType;
  id: string;
  /** People only: "deactivate" (the default) or "delete". */
  action?: 'deactivate' | 'delete';
}

export interface PurgePreviewItem {
  type: PurgeType;
  id: string;
  action?: string;
  title: string | null;
  seed: boolean;
  blocked?: string;
  impact: Record<string, number>;
  notes?: string[];
}

export interface PurgePreview {
  items: PurgePreviewItem[];
  totals: Record<string, number>;
  removable: number;
  blocked: number;
}

export interface PurgeResult {
  results: Array<{ type: PurgeType; id: string; title?: string; status: 'removed' | 'blocked' | 'missing' | 'failed'; reason?: string }>;
  removed: number;
  not_removed: number;
}

export function previewPurge(items: PurgeTarget[]): Promise<PurgePreview> {
  return apiJson('/api/admin/platform/purge/preview', { auth: true, method: 'POST', body: { items } });
}

export function runPurge(items: PurgeTarget[], revokeCertificates: boolean): Promise<PurgeResult> {
  return apiJson('/api/admin/platform/purge', {
    auth: true,
    method: 'POST',
    body: { items, revoke_certificates: revokeCertificates, confirmed: true },
  });
}

export type CleanupKind =
  | 'events' | 'courses' | 'workshops' | 'kudos' | 'points' | 'badges' | 'certificates' | 'comments' | 'users';

export const CLEANUP_KINDS: CleanupKind[] = [
  'events', 'courses', 'workshops', 'kudos', 'points', 'badges', 'certificates', 'comments', 'users',
];

export interface CleanupFilters {
  q?: string;
  person?: string;
  from?: string;
  to?: string;
  origin?: '' | 'seeded' | 'created';
}

export interface CleanupList {
  kind: CleanupKind;
  total: number;
  limit: number;
  rows: Array<Record<string, any> & { id: string; type: PurgeType; title: string | null }>;
  people: Array<{ id: string; name: string }>;
}

export function fetchCleanupSummary(): Promise<Record<CleanupKind, number>> {
  return apiJson('/api/admin/platform/cleanup', { auth: true });
}

export function fetchCleanupList(kind: CleanupKind, filters: CleanupFilters): Promise<CleanupList> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, String(v)); });
  const qs = params.toString();
  return apiJson(`/api/admin/platform/cleanup/${kind}${qs ? `?${qs}` : ''}`, { auth: true });
}
